// Importa funções necessárias do Firebase Realtime Database e Auth
import {
  getDatabase,
  ref,
  get,
  set,
  update,
  push,
  serverTimestamp,
  onValue,
  remove,
} from "firebase/database";
import {
  getAuth,
  signOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { getAnalytics, logEvent } from "firebase/analytics";
import { getHorarioInscricao, getRawVersionApp } from "./memory";
// Inicializa instâncias do Database e Auth
const db = getDatabase();
const auth = getAuth();
const analytics = getAnalytics();

/**
 * Retorna o UID do usuário atualmente logado
 */
export const getUserId = (): string | null => {
  const user = auth.currentUser;
  return user ? user.uid : null;
};

/**
 * Retorna o nome/apelido (displayName) do usuário logado, se estiver salvo no banco
 */
export const getUserDisplayName = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) return null;

  const userRef = ref(db, `users/${user.uid}`);
  const snapshot = await get(userRef);
  if (snapshot.exists()) {
    const data = snapshot.val();
    return data.displayName || null;
  }

  return null;
};

/**
 * Busca todos os usuários salvos no nó "users" do Realtime Database
 */
export const getAllUsers = async (): Promise<{ [key: string]: any } | null> => {
  try {
    const snapshot = await get(ref(db, "users"));
    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error("Erro ao buscar usuários:", error);
    return null;
  }
};

/**
 * Salva a inscrição do usuário na lista, com número de jogos e timestamp atual
 */
export const saveUserGameInfo = async (
  userId: string,
  totalJogos: number,
  isTuesday: boolean = false,
  contabilizarJogo: boolean = false,
  penalidadeJogo: boolean = false
) => {
  const db = getDatabase();
  const userRef = ref(db, `users/${userId}`);
  const timestamp = new Date();

  // 👇 Lê o estado atual primeiro
  const snapshot = await get(userRef);
  const currentData = snapshot.val() || {};
  const penalidadeAtual = currentData.penalidadeJogo || false;

  // 👇 Se já tem penalidade, mantém ela
  const novaPenalidade = penalidadeAtual ? true : penalidadeJogo;

  // 👇 Verifica se tem inscricao salva
  const currentInscricao = currentData.inscricao
    ? new Date(currentData.inscricao)
    : null;

  // Função para calcular apenas o tempo do dia em milissegundos
  const getTimeOfDayMs = (date: Date) => {
    return (
      date.getHours() * 3600000 +
      date.getMinutes() * 60000 +
      date.getSeconds() * 1000 +
      date.getMilliseconds()
    );
  };

  const nowMs = getTimeOfDayMs(timestamp);
  const thirteenMs = getHorarioInscricao() * 3600000; // 13:00:00 em ms = 13h * 3600000

  let menorTimestamp = currentData.inscricao; // padrão: mantém a já salva

  // Só considera mudar a inscrição se:
  // - A hora atual for maior ou igual a 13:00:00
  // - OU não existe ainda uma inscrição salva
  if (nowMs >= thirteenMs || !currentInscricao) {
    const currentMs = currentInscricao
      ? getTimeOfDayMs(currentInscricao)
      : Infinity;

    menorTimestamp =
      nowMs < currentMs ? timestamp.toISOString() : currentData.inscricao;
  }

  const updateData: any = {
    isTuesday,
    inscricao: menorTimestamp,
    totalJogos: totalJogos,
    jogoContabilizado: contabilizarJogo,
    penalidadeJogo: novaPenalidade,
  };

  await update(userRef, updateData);
  logEvent(analytics, "inscricao_realizada", {
    uid: userId,
  });
};

/**
 * Salva uma lista de presença
 */
export const addToPresenca = async (
  userId: string,
  displayName: string,
  isTuesday: boolean,
  totalJogos: number,
  aniversario: string
) => {
  const db = getDatabase();
  const presencaRef = ref(db, `presenca/${userId}`);

  await set(presencaRef, {
    displayName,
    timestamp: Date.now(),
    inscricaoNaTerca: isTuesday,
    totalJogos: totalJogos,
    aniversario: aniversario,
  });
};

/**
 * Limpa os dados de inscrição do usuário
 */
export const clearUserGameInfo = async (userId: string): Promise<void> => {
  const userRef = ref(db, `users/${userId}`);
  const presencaRef = ref(db, `presenca/${userId}`);

  const updates: any = {
    isTuesday: false,
  };

  await Promise.all([update(userRef, updates), remove(presencaRef)]);

  logEvent(analytics, "inscricao_cancelada", {
    uid: userId,
  });
};

/**
 * Realiza logout do usuário atual
 */
export const userLogoff = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Erro ao deslogar:", error);
    throw new Error("Falha ao deslogar.");
  }
};

/**
 * Altera a senha do usuário após reautenticação
 */
export const changePassword = async (
  oldPassword: string,
  newPassword: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error("Nenhum usuário logado.");

  const credential = EmailAuthProvider.credential(user.email!, oldPassword);
  await reauthenticateWithCredential(user, credential); // necessário para segurança
  await updatePassword(user, newPassword);
};

/**
 * Salva a data de aniversário do usuário
 */
export const saveUserBirthday = async (
  userId: string,
  birthday: string
): Promise<void> => {
  const userRef = ref(db, `users/${userId}`);
  await update(userRef, { aniversario: birthday });
};

/**
 * Busca a data de aniversário do usuário logado
 */
export const getUserBirthday = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) return null;

  const userRef = ref(db, `users/${user.uid}`);
  const snapshot = await get(userRef);
  if (snapshot.exists()) {
    const data = snapshot.val();
    return data?.aniversario || null;
  }

  return null;
};

/**
 * Registra uma ação no histórico do sistema.
 * @param tipo Tipo da ação: "inscricao", "cancelamento", "limpeza"
 * @param userId ID do usuário (se aplicável)
 * @param displayName Nome do usuário (se aplicável)
 * @param totalJogos Número de jogos (se aplicável)
 */
export const logHistorico = async (
  tipo: "inscricao" | "cancelamento" | "limpeza" | "delecao",
  userId: string | null,
  displayName: string | null,
  totalJogos?: number | null
): Promise<void> => {
  const timestamp = new Date().toISOString();

  const entry = {
    tipo,
    userId,
    displayName,
    totalJogos: totalJogos ?? null,
    data: timestamp,
  };

  const logRef = ref(db, "historico");
  const newLogRef = push(logRef); // cria uma chave válida automaticamente
  await set(newLogRef, entry);
};

export const getServerTime = async (): Promise<number> => {
  const db = getDatabase();
  const refTime = ref(db, "serverTime");

  // Escreve timestamp do servidor
  await set(refTime, serverTimestamp());

  return new Promise((resolve, reject) => {
    onValue(
      refTime,
      (snapshot) => {
        const timestamp = snapshot.val();
        if (timestamp) {
          resolve(timestamp);
        } else {
          reject("Erro ao obter hora do servidor.");
        }
      },
      { onlyOnce: true }
    );
  });
};

export const getUserNameById = async (uid: string): Promise<string> => {
  try {
    const snapshot = await get(ref(db, `users/${uid}/displayName`));
    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      throw new Error("Nome não encontrado");
    }
  } catch (error) {
    console.error("Erro ao buscar nome:", error);
    return "";
  }
};

// NOVO: Função para salvar a versão do App no Firebase
export const saveAppVersion = async () => {
  const version = getRawVersionApp();
  try {
    // Usando a constante que você sugeriu
    await set(ref(db, "config/siteVersion"), version);
    console.log(`✅ Versão do App (${version}) salva com sucesso no Firebase.`);
    // Opcional: Registrar a data da atualização
    await update(ref(db, "config"), {
      lastVersionUpdate: serverTimestamp(),
    });
  } catch (error) {
    console.error("❌ Erro ao salvar a versão do App no Firebase:", error);
  }
};
